'use client';

import {
  createElement,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import styles from './SplineViewer.module.css';
import {
  canUseSplineWebGL,
  loadSplineViewerScript,
  patchShaderPrecisionFormat,
} from './splineWebGL';

const DESKTOP_MQ = '(min-width: 768px)';
const WATERMARK_CSS = 'a { opacity: 0 !important; display: none !important; }';

export type SplineViewerProps = {
  desktopSrc: string;
  mobileSrc: string;
  className?: string;
};

function hideSplineWatermark(el: HTMLElement): () => void {
  let cancelled = false;
  let tries = 0;
  let intervalId = 0;

  const apply = (): boolean => {
    const root = el.shadowRoot;
    if (!root) return false;
    const sheet = new CSSStyleSheet();
    sheet.replaceSync(WATERMARK_CSS);
    const sheets = [...root.adoptedStyleSheets];
    if (sheets.includes(sheet)) return true;
    root.adoptedStyleSheets = [...sheets, sheet];
    return true;
  };

  const tick = () => {
    if (cancelled) return;
    if (apply() || tries >= 20) {
      window.clearInterval(intervalId);
      return;
    }
    tries += 1;
  };

  if (!apply()) {
    intervalId = window.setInterval(tick, 100);
  }

  return () => {
    cancelled = true;
    window.clearInterval(intervalId);
  };
}

export function SplineViewer({
  desktopSrc,
  mobileSrc,
  className,
}: SplineViewerProps) {
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const [isDesktop, setIsDesktop] = useState<boolean | null>(null);
  const [failed, setFailed] = useState(false);

  useLayoutEffect(() => {
    const mq = window.matchMedia(DESKTOP_MQ);
    setIsDesktop(mq.matches);
    const onChange = () => setIsDesktop(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    if (!canUseSplineWebGL()) {
      setFailed(true);
      return;
    }
    patchShaderPrecisionFormat();
    let cancelled = false;
    loadSplineViewerScript().catch(() => {
      if (!cancelled) setFailed(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const showViewer = !failed && isDesktop !== null;

  useEffect(() => {
    const el = wrapRef.current?.querySelector<HTMLElement>('spline-viewer');
    if (!el || !showViewer) return;

    const onError = () => setFailed(true);
    el.addEventListener('error', onError);
    const stopHide = hideSplineWatermark(el);
    return () => {
      el.removeEventListener('error', onError);
      stopHide();
    };
  }, [showViewer, isDesktop]);

  if (!showViewer) return null;

  const classNames = className
    ? `${styles.viewer} ${className}`
    : styles.viewer;

  return (
    <div ref={wrapRef} className={classNames} aria-hidden="true">
      {createElement('spline-viewer', {
        url: isDesktop ? desktopSrc : mobileSrc,
        loading: 'lazy',
      })}
    </div>
  );
}
